import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

const MAX_RETRIES = 30; // Maximum number of retries (30 seconds)

const PaymentSuccessOverlay = ({ onClose, onSuccess }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('verifying');
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const checkPaymentStatus = async (user) => {
      try {
        // Get fresh token right before request
        const idToken = await user.getIdToken(true);
        
        console.log('Checking payment status with token:', {
          sessionId,
          uid: user.uid,
          email: user.email,
          tokenLength: idToken.length,
          retryCount
        });
        
        const response = await fetch(`${import.meta.env.VITE_WORKER_URL}/api/payment-status?session_id=${sessionId}`, {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Payment verification failed:', {
            status: response.status,
            error: errorText
          });
          throw new Error(`Failed to verify payment status: ${errorText}`);
        }
        
        const data = await response.json();
        console.log('Payment status response:', data);
        
        if (data.fulfilled) {
          if (onSuccess) {
            await onSuccess(); // Call onSuccess to refresh token count
          }
          setStatus('success');
        } else if (data.status === 'failed') {
          // Payment explicitly failed
          setStatus('failed');
          setError('Your payment was not successful. Please try again.');
        } else if (retryCount >= MAX_RETRIES) {
          // Timeout after 30 seconds
          setStatus('timeout');
          setError('Payment verification timed out. If you completed the payment, your tokens will be added automatically when the payment is confirmed.');
        } else {
          // Payment still pending, retry in 1 second
          setRetryCount(prev => prev + 1);
          setTimeout(() => checkPaymentStatus(user), 1000);
        }
      } catch (err) {
        console.error('Payment status check error:', err);
        if (err.message.includes('not found')) {
          setStatus('not_found');
          setError('Payment record not found. Please contact support if you believe this is an error.');
        } else {
          setStatus('error');
          setError(err instanceof Error ? err.message : 'Failed to verify payment');
        }
      }
    };

    // Wait for auth state to be ready
    const auth = getAuth();
    console.log('Setting up auth state listener');
    
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log('Auth state changed:', {
        isAuthenticated: !!user,
        uid: user?.uid,
        email: user?.email
      });
      
      if (user) {
        checkPaymentStatus(user);
      } else {
        setStatus('error');
        setError('User not authenticated');
      }
    });

    return () => unsubscribe();
  }, [sessionId, retryCount]);

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
    navigate('/', { replace: true });
  };

  const handleRetry = () => {
    setStatus('verifying');
    setError(null);
    setRetryCount(0);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="text-center">
          {status === 'verifying' && (
            <>
              <div className="mb-4">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto"></div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Processing Payment</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Please wait while we verify your payment and add tokens to your account...
                {retryCount > 0 && (
                  <span className="block mt-2 text-sm">
                    Time remaining: {MAX_RETRIES - retryCount} seconds
                  </span>
                )}
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="mb-4">
                <div className="w-16 h-16 mx-auto bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Payment Successful!</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Your tokens have been added to your account.
              </p>
              <button
                onClick={handleClose}
                className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
              >
                Return to Chat
              </button>
            </>
          )}

          {(status === 'error' || status === 'failed' || status === 'timeout' || status === 'not_found') && (
            <>
              <div className="mb-4">
                <div className="w-16 h-16 mx-auto bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                {status === 'failed' ? 'Payment Failed' : 
                 status === 'timeout' ? 'Verification Timeout' :
                 status === 'not_found' ? 'Payment Not Found' : 'Verification Error'}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {error || 'An error occurred while verifying your payment.'}
              </p>
              <div className="space-y-2">
                {status !== 'failed' && (
                  <button
                    onClick={handleRetry}
                    className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Try Again
                  </button>
                )}
                <button
                  onClick={handleClose}
                  className="w-full bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  {status === 'failed' ? 'Close' : 'Return to Chat'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessOverlay; 